import { Fragment } from "react";
import { comLinks } from "./linksNoTexto";

/**
 * Renderizador mínimo do markdown que os agentes produzem.
 *
 * O modelo escreve em markdown naturalmente — pedir "não use markdown" no
 * prompt é briga perdida e frágil. Melhor renderizar: `**negrito**` virava
 * asterisco literal na tela.
 *
 * Cobre só o que aparece em briefing: negrito, listas com - ou * e listas
 * numeradas, mais endereços clicáveis (linksNoTexto.tsx — o link da
 * apresentação que o playbook entrega). Não é um parser de markdown — é
 * deliberadamente pequeno, pra não puxar dependência por causa de três
 * marcações. Se um dia precisar de tabela ou código, aí sim vale uma lib (o
 * AIPage já tem tabela própria).
 */

const negrito = (linha: string) =>
  linha.split(/(\*\*[^*]+\*\*)/g).map((pedaco, i) =>
    pedaco.startsWith("**") && pedaco.endsWith("**") ? (
      <strong key={i} className="font-semibold text-foreground">
        {pedaco.slice(2, -2)}
      </strong>
    ) : (
      <Fragment key={i}>{comLinks(pedaco, String(i))}</Fragment>
    ),
  );

const ITEM_LISTA = /^\s*[-*]\s+(.*)$/;
const ITEM_NUMERADO = /^\s*(\d+)[.)]\s+(.*)$/;

export const TextoMarkdown = ({ texto, className }: { texto: string; className?: string }) => {
  const blocos: React.ReactNode[] = [];
  let lista: { tipo: "ul" | "ol"; itens: string[] } | null = null;

  const fechaLista = () => {
    if (!lista) return;
    const Tag = lista.tipo;
    blocos.push(
      <Tag
        key={`l-${blocos.length}`}
        className={
          lista.tipo === "ol"
            ? "list-decimal space-y-0.5 pl-5"
            : "list-disc space-y-0.5 pl-5"
        }
      >
        {lista.itens.map((item, i) => (
          <li key={i}>{negrito(item)}</li>
        ))}
      </Tag>,
    );
    lista = null;
  };

  for (const linha of texto.split("\n")) {
    const numerado = linha.match(ITEM_NUMERADO);
    const marcador = linha.match(ITEM_LISTA);

    if (numerado) {
      if (lista?.tipo !== "ol") fechaLista();
      lista = lista ?? { tipo: "ol", itens: [] };
      lista.itens.push(numerado[2]);
      continue;
    }
    if (marcador) {
      if (lista?.tipo !== "ul") fechaLista();
      lista = lista ?? { tipo: "ul", itens: [] };
      lista.itens.push(marcador[1]);
      continue;
    }

    fechaLista();
    if (linha.trim() === "") continue;
    blocos.push(<p key={`p-${blocos.length}`}>{negrito(linha)}</p>);
  }
  fechaLista();

  return <div className={className}>{blocos}</div>;
};
